/**
 * Security Settings Page
 * Configure security policies and view security status
 * Accessible to Owner and Admin roles
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Shield,
  Lock,
  Key,
  AlertTriangle,
  Monitor,
  Users,
  Save,
  RotateCcw,
  ChevronRight,
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { toast } from '@/store/toastStore'
import { getSecurityPolicyService } from '@/services/securityPolicy'
import { DEFAULT_SECURITY_POLICY } from '@/types/securityPolicy.types'

// ============================================================================
// TYPES
// ============================================================================

interface SecuritySettings {
  passwordPolicy: {
    minLength: number
    requireUppercase: boolean
    requireLowercase: boolean
    requireNumbers: boolean
    requireSpecialChars: boolean
    expiryDays: number
    preventReuse: number
  }
  sessionPolicy: {
    maxConcurrentSessions: number
    sessionTimeoutMinutes: number
    enforceIpBinding: boolean
    requireMfa: boolean
  }
  loginPolicy: {
    maxFailedAttempts: number
    lockoutDurationMinutes: number
    allowRememberMe: boolean
  }
}

// ============================================================================
// SETTING CARD COMPONENT
// ============================================================================

function SettingCard({
  icon: Icon,
  title,
  description,
  children,
}: {
  icon: React.ElementType
  title: string
  description: string
  children: React.ReactNode
}) {
  return (
    <div className="card card-elevated p-6">
      <div className="flex items-start gap-4 mb-6">
        <div className="p-3 rounded-lg bg-primary-100 dark:bg-primary-900/30">
          <Icon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-text-primary">{title}</h3>
          <p className="text-sm text-text-secondary mt-1">{description}</p>
        </div>
      </div>
      {children}
    </div>
  )
}

// ============================================================================
// TOGGLE SETTING COMPONENT
// ============================================================================

function ToggleSetting({
  label,
  description,
  checked,
  onChange,
  disabled = false,
}: {
  label: string
  description?: string
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
}) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-surface-overlay last:border-0">
      <div>
        <p className="text-sm font-medium text-text-primary">{label}</p>
        {description && <p className="text-xs text-text-tertiary mt-0.5">{description}</p>}
      </div>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="toggle toggle-primary"
      />
    </div>
  )
}

// ============================================================================
// NUMBER SETTING COMPONENT
// ============================================================================

function NumberSetting({
  label,
  description,
  value,
  onChange,
  min,
  max,
  suffix,
  disabled = false,
}: {
  label: string
  description?: string
  value: number
  onChange: (value: number) => void
  min?: number
  max?: number
  suffix?: string
  disabled?: boolean
}) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-surface-overlay last:border-0">
      <div>
        <p className="text-sm font-medium text-text-primary">{label}</p>
        {description && <p className="text-xs text-text-tertiary mt-0.5">{description}</p>}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          min={min}
          max={max}
          disabled={disabled}
          className="input input-bordered input-sm w-24 text-right"
        />
        {suffix && <span className="text-sm text-text-tertiary">{suffix}</span>}
      </div>
    </div>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function SecuritySettingsPage() {
  const { hasPermission } = useAuth()
  const canManageSettings = hasPermission('txh.settings.update')

  // Settings state — seeded from schema defaults, then hydrated from the service.
  const [settings, setSettings] = useState<SecuritySettings>(DEFAULT_SECURITY_POLICY)

  const [hasChanges, setHasChanges] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  // Load the persisted policy on mount.
  useEffect(() => {
    let cancelled = false
    setIsLoading(true)
    getSecurityPolicyService()
      .get()
      .then((policy) => {
        if (!cancelled) {
          setSettings(policy)
          setHasChanges(false)
        }
      })
      .catch((err) => {
        const message = err instanceof Error ? err.message : 'Please try again.'
        if (!cancelled) toast.error('Could not load security policy', message)
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Update a nested setting
  const updateSetting = <K extends keyof SecuritySettings>(
    section: K,
    key: keyof SecuritySettings[K],
    value: SecuritySettings[K][keyof SecuritySettings[K]]
  ) => {
    setSettings((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value,
      },
    }))
    setHasChanges(true)
  }

  // Save settings — persists the full policy via the security-policy service.
  const handleSave = async () => {
    if (!canManageSettings) return
    setIsSaving(true)
    try {
      const saved = await getSecurityPolicyService().update({
        passwordPolicy: settings.passwordPolicy,
        sessionPolicy: settings.sessionPolicy,
        loginPolicy: settings.loginPolicy,
      })
      setSettings(saved)
      setHasChanges(false)
      toast.success('Security settings saved', 'Your policies have been updated.')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Please try again.'
      toast.error('Could not save settings', message)
    } finally {
      setIsSaving(false)
    }
  }

  // Reset to defaults (local only — Save persists)
  const handleReset = () => {
    if (!canManageSettings) return
    if (window.confirm('Reset all security settings to defaults?')) {
      setSettings(DEFAULT_SECURITY_POLICY)
      setHasChanges(true)
    }
  }

  // Calculate security score
  const calculateSecurityScore = (): { score: number; level: string; color: string } => {
    let score = 0
    const { passwordPolicy, sessionPolicy, loginPolicy } = settings

    // Password requirements
    if (passwordPolicy.minLength >= 8) score += 10
    if (passwordPolicy.minLength >= 12) score += 10
    if (passwordPolicy.requireUppercase) score += 10
    if (passwordPolicy.requireLowercase) score += 5
    if (passwordPolicy.requireNumbers) score += 10
    if (passwordPolicy.requireSpecialChars) score += 15
    if (passwordPolicy.expiryDays <= 90) score += 5
    if (passwordPolicy.preventReuse >= 5) score += 5

    // Session settings
    if (sessionPolicy.maxConcurrentSessions <= 5) score += 5
    if (sessionPolicy.sessionTimeoutMinutes <= 60) score += 5
    if (sessionPolicy.enforceIpBinding) score += 5
    if (sessionPolicy.requireMfa) score += 15

    // Login policy
    if (loginPolicy.maxFailedAttempts <= 5) score += 5

    const level = score >= 80 ? 'Strong' : score >= 50 ? 'Moderate' : 'Weak'
    const color =
      score >= 80
        ? 'text-green-600 dark:text-green-400'
        : score >= 50
          ? 'text-orange-600 dark:text-orange-400'
          : 'text-red-600 dark:text-red-400'

    return { score: Math.min(score, 100), level, color }
  }

  const securityScore = calculateSecurityScore()

  return (
    <div className="p-6 space-y-6 max-w-4xl mx-auto">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-text-primary">Security Settings</h1>
          <p className="text-text-secondary mt-1">
            Configure security policies for your organization
          </p>
        </div>
        {canManageSettings && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleReset}
              className="btn btn-ghost btn-sm gap-2"
              disabled={!hasChanges}
            >
              <RotateCcw className="w-4 h-4" />
              Reset
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="btn btn-primary btn-sm gap-2"
              disabled={!hasChanges || isSaving || isLoading}
            >
              {isSaving ? (
                <span className="loading loading-spinner loading-sm" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save Changes
            </button>
          </div>
        )}
      </div>

      {/* Security Score Card */}
      <div className="card card-elevated p-6 border-l-4 border-primary-500">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-full bg-primary-100 dark:bg-primary-900/30">
              <Shield className="w-8 h-8 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-text-primary">Security Score</h2>
              <p className="text-sm text-text-secondary">
                Based on your current security configuration
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className={`text-4xl font-bold ${securityScore.color}`}>
              {securityScore.score}%
            </p>
            <p className={`text-sm font-medium ${securityScore.color}`}>
              {securityScore.level}
            </p>
          </div>
        </div>

        {/* Security Score Bar */}
        <div className="mt-4">
          <div className="w-full h-3 bg-surface-overlay rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                securityScore.score >= 80
                  ? 'bg-green-500'
                  : securityScore.score >= 50
                  ? 'bg-orange-500'
                  : 'bg-red-500'
              }`}
              style={{ width: `${securityScore.score}%` }}
            />
          </div>
        </div>

        {securityScore.score < 80 && (
          <div className="mt-4 flex items-start gap-2 text-sm text-text-secondary">
            <AlertTriangle className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
            <p>
              Enable MFA and use stronger password requirements to improve your security score.
            </p>
          </div>
        )}
      </div>

      {/* Password Policy */}
      <SettingCard
        icon={Key}
        title="Password Policy"
        description="Configure password requirements for all users"
      >
        <div className="space-y-1">
          <NumberSetting
            label="Minimum Length"
            description="Minimum number of characters required"
            value={settings.passwordPolicy.minLength}
            onChange={(v) => updateSetting('passwordPolicy', 'minLength', v)}
            min={6}
            max={32}
            suffix="characters"
            disabled={!canManageSettings}
          />
          <ToggleSetting
            label="Require Uppercase Letters"
            description="At least one uppercase letter (A-Z)"
            checked={settings.passwordPolicy.requireUppercase}
            onChange={(v) => updateSetting('passwordPolicy', 'requireUppercase', v)}
            disabled={!canManageSettings}
          />
          <ToggleSetting
            label="Require Lowercase Letters"
            description="At least one lowercase letter (a-z)"
            checked={settings.passwordPolicy.requireLowercase}
            onChange={(v) => updateSetting('passwordPolicy', 'requireLowercase', v)}
            disabled={!canManageSettings}
          />
          <ToggleSetting
            label="Require Numbers"
            description="At least one number (0-9)"
            checked={settings.passwordPolicy.requireNumbers}
            onChange={(v) => updateSetting('passwordPolicy', 'requireNumbers', v)}
            disabled={!canManageSettings}
          />
          <ToggleSetting
            label="Require Special Characters"
            description="At least one special character (!@#$%...)"
            checked={settings.passwordPolicy.requireSpecialChars}
            onChange={(v) => updateSetting('passwordPolicy', 'requireSpecialChars', v)}
            disabled={!canManageSettings}
          />
          <NumberSetting
            label="Password Expiry"
            description="Days until password must be changed (0 = never)"
            value={settings.passwordPolicy.expiryDays}
            onChange={(v) => updateSetting('passwordPolicy', 'expiryDays', v)}
            min={0}
            max={365}
            suffix="days"
            disabled={!canManageSettings}
          />
          <NumberSetting
            label="Prevent Password Reuse"
            description="Number of previous passwords to remember"
            value={settings.passwordPolicy.preventReuse}
            onChange={(v) => updateSetting('passwordPolicy', 'preventReuse', v)}
            min={0}
            max={24}
            suffix="passwords"
            disabled={!canManageSettings}
          />
        </div>
      </SettingCard>

      {/* Session Policy */}
      <SettingCard
        icon={Monitor}
        title="Session Policy"
        description="Control how user sessions are managed"
      >
        <div className="space-y-1">
          <NumberSetting
            label="Maximum Concurrent Sessions"
            description="Max active sessions per user"
            value={settings.sessionPolicy.maxConcurrentSessions}
            onChange={(v) => updateSetting('sessionPolicy', 'maxConcurrentSessions', v)}
            min={1}
            max={20}
            suffix="sessions"
            disabled={!canManageSettings}
          />
          <NumberSetting
            label="Session Timeout"
            description="Auto-logout after inactivity"
            value={settings.sessionPolicy.sessionTimeoutMinutes}
            onChange={(v) => updateSetting('sessionPolicy', 'sessionTimeoutMinutes', v)}
            min={5}
            max={1440}
            suffix="minutes"
            disabled={!canManageSettings}
          />
          <ToggleSetting
            label="Enforce IP Binding"
            description="Sessions tied to original IP address"
            checked={settings.sessionPolicy.enforceIpBinding}
            onChange={(v) => updateSetting('sessionPolicy', 'enforceIpBinding', v)}
            disabled={!canManageSettings}
          />
          <ToggleSetting
            label="Require Multi-Factor Authentication"
            description="All users must enable MFA (recommended)"
            checked={settings.sessionPolicy.requireMfa}
            onChange={(v) => updateSetting('sessionPolicy', 'requireMfa', v)}
            disabled={!canManageSettings}
          />
        </div>
      </SettingCard>

      {/* Login Policy */}
      <SettingCard
        icon={Lock}
        title="Login Policy"
        description="Configure login security and lockout settings"
      >
        <div className="space-y-1">
          <NumberSetting
            label="Maximum Failed Attempts"
            description="Lock account after consecutive failures"
            value={settings.loginPolicy.maxFailedAttempts}
            onChange={(v) => updateSetting('loginPolicy', 'maxFailedAttempts', v)}
            min={1}
            max={20}
            suffix="attempts"
            disabled={!canManageSettings}
          />
          <NumberSetting
            label="Lockout Duration"
            description="How long accounts stay locked"
            value={settings.loginPolicy.lockoutDurationMinutes}
            onChange={(v) => updateSetting('loginPolicy', 'lockoutDurationMinutes', v)}
            min={1}
            max={1440}
            suffix="minutes"
            disabled={!canManageSettings}
          />
          <ToggleSetting
            label="Allow Remember Me"
            description="Users can stay logged in longer"
            checked={settings.loginPolicy.allowRememberMe}
            onChange={(v) => updateSetting('loginPolicy', 'allowRememberMe', v)}
            disabled={!canManageSettings}
          />
        </div>
      </SettingCard>

      {/* Quick Links */}
      <div className="card card-elevated p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4">Related Security Actions</h3>
        <div className="space-y-2">
          <Link
            to="/sessions"
            className="flex items-center justify-between p-3 rounded-lg border border-surface-overlay hover:border-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/10 transition-colors group"
          >
            <div className="flex items-center gap-3">
              <Monitor className="w-5 h-5 text-text-secondary group-hover:text-primary-600 dark:group-hover:text-primary-400" />
              <div>
                <p className="text-sm font-medium text-text-primary">Active Sessions</p>
                <p className="text-xs text-text-tertiary">View and manage active sessions</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-text-tertiary group-hover:text-primary-600 dark:group-hover:text-primary-400" />
          </Link>

          <Link
            to="/audit-log"
            className="flex items-center justify-between p-3 rounded-lg border border-surface-overlay hover:border-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/10 transition-colors group"
          >
            <div className="flex items-center gap-3">
              <Shield className="w-5 h-5 text-text-secondary group-hover:text-primary-600 dark:group-hover:text-primary-400" />
              <div>
                <p className="text-sm font-medium text-text-primary">Audit Log</p>
                <p className="text-xs text-text-tertiary">View security events and activity</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-text-tertiary group-hover:text-primary-600 dark:group-hover:text-primary-400" />
          </Link>

          <Link
            to="/users"
            className="flex items-center justify-between p-3 rounded-lg border border-surface-overlay hover:border-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/10 transition-colors group"
          >
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-text-secondary group-hover:text-primary-600 dark:group-hover:text-primary-400" />
              <div>
                <p className="text-sm font-medium text-text-primary">User Management</p>
                <p className="text-xs text-text-tertiary">Manage users and permissions</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-text-tertiary group-hover:text-primary-600 dark:group-hover:text-primary-400" />
          </Link>
        </div>
      </div>

      {/* Unsaved Changes Warning */}
      {hasChanges && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40">
          <div className="card card-elevated p-4 shadow-2xl flex items-center gap-4 border-l-4 border-orange-500">
            <AlertTriangle className="w-5 h-5 text-orange-500" />
            <span className="text-sm text-text-primary">You have unsaved changes</span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setHasChanges(false)}
                className="btn btn-ghost btn-sm"
              >
                Discard
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="btn btn-primary btn-sm"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
